import { CloseOutlined } from "@ant-design/icons";
import FilledCheckIcon from "../../../core/svg-icons/FilledCheckIcon";

const styles = `
.timeline-modal .modal-content{
  border-radius:16px;
  border:none;
  box-shadow:0 12px 35px rgba(0,0,0,.15);
}

.timeline-modal .modal-header{
  border-bottom:1px solid #edf0f2;
  padding:18px 24px;
}

.timeline-modal .modal-body{
  background:#fafbfc;
}

.timeline-wrapper{
  position:relative;
  padding-left:32px;
}

.timeline-wrapper::before{
  content:"";
  position:absolute;
  left:13px;
  top:0;
  bottom:0;
  width:2px;
  background:#d8dee9;
}

.timeline-item{
  position:relative;
  margin-bottom:22px;
}

.timeline-icon{
  position:absolute;
  left:-31px;
  top:8px;
  width:28px;
  height:28px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#fff;
  border-radius:50%;
  z-index:2;
}

.timeline-card{
  background:#fff;
  border:1px solid #e9ecef;
  border-radius:12px;
  padding:16px 18px;
  transition:.2s;
}

.timeline-card:hover{
  box-shadow:0 8px 20px rgba(0,0,0,.08);
}

.timeline-title{
  font-size:15px;
  font-weight:600;
  color:#212529;
}

.timeline-entity{
  font-size:14px;
  color:#495057;
  margin-top:4px;
}

.timeline-meta{
  font-size:13px;
  color:#6c757d;
  margin-top:10px;
}

.timeline-reason{
  background:#f8f9fa;
  border-radius:8px;
  padding:10px;
  margin-top:10px;
  font-size:13px;
}

.timeline-empty{
  display:flex;
  align-items:center;
  justify-content:center;
  min-height:220px;
  color:#6c757d;
  font-weight:500;
}

.timeline-loading{
  display:flex;
  justify-content:center;
  align-items:center;
  min-height:220px;
}

.timeline-loading .spinner-border{
  width:2rem;
  height:2rem;
}
`;

export interface TimelineEvent {
  event_id: string;
  title?: string;
  description?: string;
  actor_name: string;
  actor_type: string;
  event_type: string;

  // Case Timeline
  entity_id?: string;
  entity_name?: string;
  case_id?: string;
  occurred_at?: string;

  // Check Timeline
  event_time?: string;

  diff?: Record<string, any>;
}

const formatEventType = (eventType?: string) => {
  if (!eventType) return "-";

  return eventType
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

interface ProgressProp {
  data: TimelineEvent;
  size?: string;
  statusMap?: Record<string, string>;
}

const ProgressContent: React.FC<ProgressProp> = ({
  data,
  size = "",
  statusMap = {},
}) => {
  const occurredDate = data?.occurred_at
    ? new Date(data.occurred_at)
    : data?.event_time
      ? new Date(data.event_time)
      : null;

  const dateStr = occurredDate ? occurredDate.toLocaleDateString() : "-";

  const timeStr = occurredDate
    ? occurredDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  const isStatusChange =
    data?.event_type === "CASE_STATUS_CHANGED" ||
    data?.event_type === "CHECK_STATUS_CHANGED";


  // previous status
  const prev_status = data?.diff?.from_status || data?.diff?.old_status || "";
  const fromStatus = prev_status
    ? (statusMap[prev_status] ??
      prev_status?.replaceAll("_", " "))
    : "-";

  // current status
  const current_status = data?.diff?.to_status || data?.diff?.new_status || "";
  const toStatus = current_status
    ? (statusMap[current_status] ??
      current_status?.replaceAll("_", " "))
    : "-";

  return (
    <div className="timeline-item">
      <div className="timeline-icon">
        <FilledCheckIcon size={size} />
      </div>

      <div className="timeline-card">
        <div className="timeline-title">
          {data?.title ??
            data?.description ??
            formatEventType(data?.event_type)}
        </div>

        {(data?.entity_name || data?.description) && (
          <div className="timeline-entity">
            {data.entity_name ?? data.description}
          </div>
        )}

        {/* {isStatusChange && (
          <div className="d-flex align-items-center gap-2 mt-3">
            <span className="badge bg-secondary">{fromStatus}</span>

            <i className="ti ti-arrow-right text-success"></i>

            <span className="badge bg-success">{toStatus}</span>
          </div>
        )} */}
        {isStatusChange && (
          <div className="d-flex align-items-center gap-2 mt-3">
            <span className="badge bg-secondary">{fromStatus}</span>

            <i className="ti ti-arrow-right text-success"></i>

            <span className="badge bg-success">{toStatus}</span>
          </div>
        )}

        <div className="timeline-meta">
          <i className="ti ti-user me-1"></i>
          {data?.actor_name ?? "System"} ({data?.actor_type})
          <span className="mx-2">•</span>
          <i className="ti ti-calendar me-1"></i>
          {dateStr}
          <span className="mx-2">•</span>
          <i className="ti ti-clock me-1"></i>
          {timeStr}
        </div>

        {(data?.diff?.action_reason ||
          data?.diff?.remarks ||
          data?.diff?.reason) && (
          <div className="timeline-reason">
            <strong>Reason:</strong>{" "}
            {data?.diff?.action_reason ??
              data?.diff?.remarks ??
              data?.diff?.reason}
          </div>
        )}
      </div>
    </div>
  );
};

const CaseTimelineModal = ({
  onClose,
  timelineData = [],
  title = null,
  maxWidth = null,
  maxHeight = null,
  loading = false,
  statusMap = {},
}: {
  onClose: () => void;
  timelineData?: any[];
  title?: string | null;
  maxWidth?: number | null;
  maxHeight?: number | null;
  loading?: boolean;
  statusMap?: Record<string, string>;
}) => {
  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show timeline-modal"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content mx-auto"
            style={{
              minWidth: 600,
              maxWidth: maxWidth || 750,
              maxHeight: maxHeight || 600,
            }}
          >
            <div className="modal-header">
              <h4 className="fw-bold mb-0">Timeline</h4> 
               <button
                  type="button"
                  className="caf-btn-close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <CloseOutlined />
                </button>
            </div>

            {title && <div className="px-4 pt-3 fw-semibold">{title}</div>}

            <div className="modal-body overflow-auto">
              {loading ? (
                <div className="timeline-loading">
                  <div className="spinner-border text-success" />
                </div>
              ) : timelineData.length ? (
                <div className="timeline-wrapper">
                  {timelineData.map((item, index) => (
                    <ProgressContent
                      key={`${item.event_id}-${index}`}
                      data={item}
                      statusMap={statusMap}
                    />
                  ))}
                </div>
              ) : (
                <div className="timeline-empty">No timeline available</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{styles}</style>
    </>
  );
};

export default CaseTimelineModal;

// import FilledCheckIcon from "../../core/svg-icons/FilledCheckIcon";

// const styles = `
//   .progress-wrapper {
//      border-left: 3px solid rgba(25, 135, 84, 0.8);
//    }
//   .progress-container {
//     position: relative;
//   }
//   .progress-icon {
//     position: absolute;
//     z-index: 20;
//     left: -2px;
//     top: -2px;
//     transform: translateX(-50%);
//     border-radius: 50%;
//     background: white;
//   }
// `;

// export interface TimelineEvent {
//   event_id: string;
//   actor_name: string;
//   actor_type: string;
//   event_type: string;
//   entity_id: string;
//   entity_name: string;
//   case_id: string;
//   diff?: Record<string, any>;
//   occurred_at: string;
// }

// const formatEventType = (eventType?: string) => {
//   if (!eventType) return "-";
//   return eventType
//     .toLowerCase()
//     .split("_")
//     .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//     .join(" ");
// };

// interface ProgressProp {
//   data: TimelineEvent;
//   size?: string;
//   statusMap?: Record<string, string>; // status_id -> status_name
// }

// // single event/step
// const ProgressContent: React.FC<ProgressProp> = ({
//   data,
//   size = "",
//   statusMap = {},
// }) => {
//   const occurredDate = data?.occurred_at ? new Date(data.occurred_at) : null;
//   const dateStr = occurredDate ? occurredDate.toLocaleDateString() : "-";
//   const timeStr = occurredDate
//     ? occurredDate.toLocaleTimeString([], {
//         hour: "2-digit",
//         minute: "2-digit",
//       })
//     : "-";

//   const isStatusChange = data?.event_type === "CASE_STATUS_CHANGED";
//   const fromStatusId = data?.diff?.from_status;
//   const toStatusId = data?.diff?.to_status;
//   const fromStatusLabel = fromStatusId
//     ? statusMap[fromStatusId] ?? fromStatusId
//     : "-";
//   const toStatusLabel = toStatusId ? statusMap[toStatusId] ?? toStatusId : "-";
//   const actionReason = data?.diff?.action_reason;

//   return (
//     <div className="progress-wrapper mt-2">
//       <div className="progress-container ps-4">
//         <div className="progress-icon">
//           <FilledCheckIcon size={size} />
//         </div>
//         <div className="text-dark pb-3">
//           <p className="fw-bold mb-1">{formatEventType(data?.event_type)}</p>
//           {data?.entity_name && <div>{data.entity_name}</div>}

//           {isStatusChange && (
//             <div className="d-flex align-items-center gap-2 my-1">
//               <span className="badge bg-secondary">{fromStatusLabel}</span>
//               <i className="ti ti-arrow-right" />
//               <span className="badge bg-success">{toStatusLabel}</span>
//             </div>
//           )}

//           <div className="text-muted small">
//             By {data?.actor_name ?? "System"} ({data?.actor_type}) on{" "}
//             {dateStr}, {timeStr}
//           </div>

//           {isStatusChange && actionReason && (
//             <div className="small mt-1">
//               <span className="fw-semibold">Reason: </span>
//               {actionReason}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// // main component
// const TimelineModal = ({
//   onClose,
//   timelineData = [],
//   title = null,
//   maxWidth = null,
//   maxHeight = null,
//   loading = false,
//   statusMap = {},
// }: {
//   onClose: () => void;
//   timelineData?: any[];
//   title?: string | null;
//   maxWidth?: number | null;
//   maxHeight?: number | null;
//   loading?: boolean;
//   statusMap?: Record<string, string>;
// }) => {
//   return (
//     <>
//       <div className="modal-backdrop fade show" onClick={onClose} />
//       <div
//         className="modal fade show"
//         style={{ display: "block" }}
//         role="dialog"
//         aria-modal="true"
//       >
//         <div className="modal-dialog modal-dialog-centered modal-xl">
//           <div
//             className="modal-content mx-auto overflow-hidden"
//             style={{
//               maxHeight: maxHeight || 450,
//               maxWidth: maxWidth || 700,
//               height: maxHeight || "auto",
//             }}
//           >
//             <div className="modal-header">
//               <h3 className="fw-bold">Timeline</h3>
//               <button
//                 type="button"
//                 className="btn-close custom-btn-close"
//                 onClick={onClose}
//                 aria-label="Close"
//               >
//                 <i className="ti ti-x" />
//               </button>
//             </div>
//             {title && (
//               <div className="px-3 pt-3 fw-semibold">{title} - Timeline</div>
//             )}
//             <div className="modal-body px-4 mt-3 mb-4 overflow-y-auto">
//               {loading ? (
//                 <div>Loading...</div>
//               ) : timelineData && timelineData.length > 0 ? (
//                 timelineData.map((d, idx) => (
//                   <ProgressContent
//                     key={`${d.event_id}-${d.entity_id}-${idx}`}
//                     data={d}
//                     statusMap={statusMap}
//                   />
//                 ))
//               ) : (
//                 <div>No timeline available</div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style>{styles}</style>
//     </>
//   );
// };

// export default TimelineModal;

// import FilledCheckIcon from "../../core/svg-icons/FilledCheckIcon";

// const styles = `
//   .progress-wrapper {
//      border-left: 3px solid rgba(25, 135, 84, 0.8);
//    }
//   .progress-container {
//     position: relative;
//   }
//   .progress-icon {
//     position: absolute;
//     z-index: 20;
//     left: -2px;
//     top: -2px;
//     transform: translateX(-50%);
//     border-radius: 50%;
//     background: white;
//   }
// `;

// export interface TimelineEvent {
//   event_id: string;
//   actor_name: string;
//   actor_type: string;
//   event_type: string;
//   entity_id: string;
//   entity_name: string;
//   case_id: string;
//   diff?: Record<string, any>;
//   occurred_at: string;
// }

// const formatEventType = (eventType?: string) => {
//   if (!eventType) return "-";
//   return eventType
//     .toLowerCase()
//     .split("_")
//     .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//     .join(" ");
// };

// interface ProgressProp {
//   data: TimelineEvent;
//   size?: string;
// }

// // single event/step
// const ProgressContent: React.FC<ProgressProp> = ({ data, size = "" }) => {
//   const occurredDate = data?.occurred_at ? new Date(data.occurred_at) : null;
//   const dateStr = occurredDate ? occurredDate.toLocaleDateString() : "-";
//   const timeStr = occurredDate
//     ? occurredDate.toLocaleTimeString([], {
//         hour: "2-digit",
//         minute: "2-digit",
//       })
//     : "-";

//   return (
//     <div className="progress-wrapper mt-2">
//       <div className="progress-container ps-4">
//         <div className="progress-icon">
//           <FilledCheckIcon size={size} />
//         </div>
//         <div className="text-dark pb-3">
//           <p className="fw-bold mb-1">{formatEventType(data?.event_type)}</p>
//           {data?.entity_name && <div>{data.entity_name}</div>}
//           <div className="text-muted small">
//             By {data?.actor_name} ({data?.actor_type}) on {dateStr}, {timeStr}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // main component
// const TimelineModal = ({
//   onClose,
//   timelineData = [],
//   title = null,
//   maxWidth = null,
//   maxHeight = null,
//   loading = false,
// }: {
//   onClose: () => void;
//   timelineData?: any[];
//   title?: string | null;
//   maxWidth?: number | null;
//   maxHeight?: number | null;
//   loading?: boolean;
// }) => {
//   return (
//     <>
//       <div className="modal-backdrop fade show" onClick={onClose} />
//       <div
//         className="modal fade show"
//         style={{ display: "block" }}
//         role="dialog"
//         aria-modal="true"
//       >
//         <div className="modal-dialog modal-dialog-centered modal-xl">
//           <div
//             className="modal-content mx-auto overflow-hidden"
//             style={{
//               maxHeight: maxHeight || 450,
//               maxWidth: maxWidth || 700,
//               height: maxHeight || "auto",
//             }}
//           >
//             <div className="modal-header">
//               <h3 className="fw-bold">Timeline</h3>
//               <button
//                 type="button"
//                 className="btn-close custom-btn-close"
//                 onClick={onClose}
//                 aria-label="Close"
//               >
//                 <i className="ti ti-x" />
//               </button>
//             </div>
//             {title && (
//               <div className="px-3 pt-3 fw-semibold">{title} - Timeline</div>
//             )}
//             <div className="modal-body px-4 mt-3 mb-4 overflow-y-auto">
//               {loading ? (
//                 <div>Loading...</div>
//               ) : timelineData && timelineData.length > 0 ? (
//                 timelineData.map((d, idx) => (
//                   <ProgressContent
//                     key={`${d.event_id}-${d.entity_id}-${idx}`}
//                     data={d}
//                   />
//                 ))
//               ) : (
//                 <div>No timeline available</div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style>{styles}</style>
//     </>
//   );
// };

// export default TimelineModal;

// // import FilledCheckIcon from "../../core/svg-icons/FilledCheckIcon";

// // const styles = `
// //   .progress-wrapper {
// //      border-left: 3px solid rgba(25, 135, 84, 0.8);
// //    }
// //   .progress-container {
// //     position: relative;
// //   }
// //   .progress-icon {
// //     position: absolute;
// //     z-index: 20;
// //     left: -2px;
// //     top: -2px;
// //     transform: translateX(-50%);
// //     border-radius: 50%;
// //     background: white;
// //   }
// // `;

// // interface ProgressProp {
// //   data: any;
// //   size?: string;
// //   isChild?: boolean;
// // }

// // // ProgressContent
// // const ProgressContent: React.FC<ProgressProp> = ({
// //   data,
// //   size = "",
// //   isChild = false,
// // }) => {
// //   return (
// //     <>
// //       <div className="progress-wrapper mt-2">
// //         <div className="progress-container ps-4">
// //           <div className="progress-icon">
// //             <FilledCheckIcon size={size} />
// //           </div>
// //           {isChild ? (
// //             <div className="text-dark pb-3">
// //               <div>
// //                 {data?.title} sent on {data?.date}
// //               </div>
// //               <div>
// //                 {data?.date}, {data?.time}
// //               </div>
// //             </div>
// //           ) : (
// //             <p className="fw-bold text-dark pb-3">
// //               {data?.title} Date {data?.date}, {data?.time}
// //             </p>
// //           )}
// //         </div>

// //         {/* if nested data */}
// //         <div className="ms-4 ps-2">
// //           {data?.children?.length > 0 &&
// //             data?.children?.map((d: any) => (
// //               <ProgressContent data={d} size={"20px"} isChild={true} />
// //             ))}
// //         </div>
// //       </div>
// //     </>
// //   );
// // };

// // // main component
// // const TimelineModal = ({
// //   onClose,
// //   timelineData = null,
// //   title = null,
// //   maxWidth = null,
// //   maxHeight = null,
// // }: {
// //   onClose: () => void;
// //   timelineData?: any;
// //   title?: string | null;
// //   maxWidth?: number | null;
// //   maxHeight?: number | null;
// // }) => {
// //   return (
// //     <>
// //       <div className="modal-backdrop fade show" onClick={onClose} />
// //       <div
// //         className="modal fade show"
// //         style={{ display: "block" }}
// //         role="dialog"
// //         aria-modal="true"
// //       >
// //         <div className={`modal-dialog modal-dialog-centered modal-xl `}>
// //           <div
// //             className="modal-content mx-auto overflow-hidden"
// //             style={{
// //               maxHeight: maxHeight || 450,
// //               maxWidth: maxWidth || 700,
// //               height: maxHeight || "auto",
// //             }}
// //           >
// //             <div className="modal-header">
// //               <h3 className="fw-bold ">Timeline</h3>
// //               <button
// //                 type="button"
// //                 className="btn-close custom-btn-close"
// //                 onClick={onClose}
// //                 aria-label="Close"
// //               >
// //                 <i className="ti ti-x" />
// //               </button>
// //             </div>
// //             {title && (
// //               <div className="px-3 pt-3 fw-semibold">{title} - Timeline</div>
// //             )}
// //             <div className="modal-body px-4 mt-3 mb-4 overflow-y-auto">
// //               {timelineData ? (
// //                 timelineData?.map((d: any) => <ProgressContent data={d} />)
// //               ) : (
// //                 <div>No timeline available</div>
// //               )}
// //             </div>
// //           </div>
// //         </div>
// //       </div>
// //       <style>{styles}</style>
// //     </>
// //   );
// // };

// // export default TimelineModal;
